import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { BadgeCheck, ChevronDown, ChevronUp, BookOpen, Check } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ThreadView = ({ threadId, onClose }) => {
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        const response = await axios.get(`${API_URL}/posts/threads/${threadId}`, {
          withCredentials: true
        });
        setThread(response.data);
      } catch (error) {
        toast.error('Failed to load thread');
      } finally {
        setLoading(false);
      }
    };

    if (threadId) {
      fetchThread();
    }
  }, [threadId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="text-center py-8 text-zinc-500">
        Thread not found
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="thread-view">
      {/* Thread Header */}
      <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-purple-500" />
            <div>
              <h3 className="font-bold text-zinc-900 dark:text-white">
                {thread.title || 'Thread'}
              </h3>
              <p className="text-sm text-zinc-500">
                {thread.posts_count} posts • {thread.likes_count || 0} likes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {thread.is_complete && (
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium flex items-center gap-1">
                <Check className="w-3 h-3" /> Complete
              </span>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
            >
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Thread Author */}
        <div className="flex items-center gap-2 mt-3">
          <Avatar className="w-6 h-6">
            <AvatarImage src={thread.user?.avatar} />
            <AvatarFallback className="bg-primary text-white text-xs">
              {thread.user?.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <span className="font-medium text-sm">{thread.user?.name}</span>
          {thread.user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
          <span className="text-zinc-500 text-sm">@{thread.user?.username}</span>
        </div>
      </div>

      {/* Thread Posts */}
      {expanded && (
        <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {thread.posts_data?.map((post, idx) => (
            <div 
              key={post.post_id}
              className="p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors relative"
              onClick={() => navigate(`/post/${post.post_id}`)}
            >
              {/* Thread line */}
              {idx < thread.posts_data.length - 1 && (
                <div className="absolute left-7 top-12 bottom-0 w-0.5 bg-purple-200 dark:bg-purple-800" />
              )}

              {/* Post number badge */}
              <div className="absolute left-4 top-4 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 flex items-center justify-center text-xs font-bold">
                {idx + 1}
              </div>

              <div className="pl-10">
                <p className="text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">
                  {post.content}
                </p>

                {/* Post Media */}
                {post.video_url && (
                  <div className="mt-2 relative rounded-lg overflow-hidden bg-black">
                    <video
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                      className="w-full max-h-48 object-contain"
                      controls
                    />
                  </div>
                )}

                {post.images && post.images.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {post.images.slice(0, 4).map((img, i) => (
                      <img 
                        key={i} 
                        src={img} 
                        alt="" 
                        className="rounded-lg w-full object-cover max-h-32"
                      />
                    ))}
                  </div>
                )}

                {post.audio_url && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <audio
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                      controls
                      className="w-full h-10"
                      preload="metadata"
                    />
                  </div>
                )}

                {post.image_url && (!post.images || post.images.length === 0) && (
                  <img src={post.image_url} alt="" className="mt-2 rounded-lg w-full object-cover max-h-32" />
                )}

                {post.gif_url && (
                  <img src={post.gif_url} alt="GIF" className="mt-2 max-h-32 rounded-lg" />
                )}

                {/* Post Time */}
                <p className="text-xs text-zinc-500 mt-2">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThreadView;

// Thread indicator badge for feed posts
export const ThreadBadge = ({ threadId, postsCount, onClick }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(threadId);
      }}
      className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-xs font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
      data-testid="thread-badge"
    >
      <BookOpen className="w-3 h-3" />
      Thread • {postsCount} posts
    </button>
  );
};
